import React, { useEffect, useMemo, useState } from 'react';
import { View, TouchableOpacity, Modal, FlatList, StyleSheet, Platform, ViewStyle, TextInput, KeyboardAvoidingView } from 'react-native';
import { useTheme } from '../../theme/ThemeContext';
import { Theme } from '../../theme/colors';
import ThemedText from './ThemedText';

interface PickerItem {
  label: string;
  value: string | undefined;
}

interface CustomPickerProps {
  selectedValue: string | undefined;
  onValueChange: (value: string | undefined) => void;
  items: PickerItem[];
  style?: ViewStyle;
  placeholder?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  disabled?: boolean;
  themeOverrides?: Partial<Theme>;
  forceDarkAppearance?: boolean;
}

export const CustomPicker: React.FC<CustomPickerProps> = ({
  selectedValue,
  onValueChange,
  items,
  style,
  placeholder = 'Select an option',
  searchable = false,
  searchPlaceholder = 'Search...',
  disabled = false,
  themeOverrides,
  forceDarkAppearance,
}) => {
  const [modalVisible, setModalVisible] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { theme, isDark } = useTheme();
  const pickerTheme = useMemo(() => {
    if (!themeOverrides) {
      return theme;
    }
    return { ...theme, ...themeOverrides };
  }, [theme, themeOverrides]);
  const useDarkAppearance = forceDarkAppearance ?? isDark;

  useEffect(() => {
    if (!modalVisible) {
      setSearchQuery('');
    }
  }, [modalVisible]);

  const filteredItems = useMemo(() => {
    if (!searchable) {
      return items;
    }

    const query = searchQuery.trim().toLowerCase();
    if (!query) {
      return items;
    }

    return items.filter(item => item.label.toLowerCase().includes(query));
  }, [items, searchable, searchQuery]);

  const selectedItem = items.find(item => item.value === selectedValue);
  const displayText = selectedItem ? selectedItem.label : placeholder;

  const handleSelect = (value: string | undefined) => {
    onValueChange(value);
    setModalVisible(false);
  };

  return (
    <View style={style}>
      <TouchableOpacity
        style={[
          styles.pickerButton, 
          { 
            borderColor: pickerTheme.border, 
            backgroundColor: disabled ? pickerTheme.disabled : pickerTheme.backgroundSecondary,
            opacity: disabled ? 0.5 : 1,
          }
        ]}
        onPress={() => !disabled && setModalVisible(true)}
        disabled={disabled}
      >
        <ThemedText style={[styles.pickerText, { color: selectedValue ? pickerTheme.text : pickerTheme.placeholder }, !selectedValue && styles.placeholderText]}>
          {displayText}
        </ThemedText>
        <ThemedText style={[styles.arrow, { color: pickerTheme.textSecondary }]}>▼</ThemedText>
      </TouchableOpacity>

      <Modal
        visible={modalVisible}
        transparent={true}
        animationType="slide"
        onRequestClose={() => setModalVisible(false)}
      >
        <KeyboardAvoidingView
          behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
          style={{ flex: 1 }}
        >
          <TouchableOpacity
            style={styles.modalOverlay}
            activeOpacity={1}
            onPress={() => setModalVisible(false)}
          >
          <TouchableOpacity 
            style={[styles.modalContent, { backgroundColor: pickerTheme.card }]}
            activeOpacity={1}
            onPress={(e) => {
              // Prevent clicks inside the modal from closing it
              e.stopPropagation();
            }}
          >
            <View style={[styles.modalHeader, { borderBottomColor: pickerTheme.border }]}>
              <ThemedText style={[styles.modalTitle, { color: pickerTheme.text }]}>{placeholder}</ThemedText>
              <TouchableOpacity
                onPress={() => setModalVisible(false)}
                style={styles.closeButton}
              >
                <ThemedText style={[styles.closeButtonText, { color: pickerTheme.textSecondary }]}>✕</ThemedText>
              </TouchableOpacity>
            </View>

            {searchable && (
              <View style={[styles.searchContainer, { borderBottomColor: pickerTheme.borderLight, backgroundColor: pickerTheme.backgroundSecondary }]}> 
                <TextInput
                  style={[styles.searchInput, { color: pickerTheme.text }]}
                  placeholder={searchPlaceholder}
                  placeholderTextColor={pickerTheme.placeholder}
                  value={searchQuery}
                  onChangeText={setSearchQuery}
                  autoCorrect={false}
                  autoCapitalize="none"
                />
              </View>
            )}
            <FlatList
              data={filteredItems}
              keyExtractor={(item, index) => `${item.value}-${index}`}
              renderItem={({ item }) => (
                <TouchableOpacity
                  style={[
                    styles.option,
                    { borderBottomColor: pickerTheme.borderLight },
                    item.value === selectedValue && { backgroundColor: useDarkAppearance ? pickerTheme.backgroundTertiary : '#F0F7FF' },
                  ]}
                  onPress={() => handleSelect(item.value)}
                >
                  <ThemedText
                    style={[
                      styles.optionText,
                      { color: pickerTheme.text },
                      item.value === selectedValue && { color: pickerTheme.accent, fontWeight: '600' },
                    ]}
                  >
                    {item.label}
                  </ThemedText>
                  {item.value === selectedValue && (
                    <ThemedText style={[styles.checkmark, { color: pickerTheme.accent }]}>✓</ThemedText>
                  )}
                </TouchableOpacity>
              )}
              keyboardShouldPersistTaps="handled"
              ListEmptyComponent={
                <View style={styles.emptyState}>
                  <ThemedText style={[styles.emptyText, { color: pickerTheme.textSecondary }]}>No results found</ThemedText>
                </View>
              }
            />
          </TouchableOpacity>
        </TouchableOpacity>
        </KeyboardAvoidingView>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  pickerButton: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: 12,
    borderWidth: 1,
    borderRadius: 8,
  },
  pickerText: {
    fontSize: 16,
    flex: 1,
  },
  placeholderText: {
  },
  arrow: {
    fontSize: 12,
    marginLeft: 8,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-start',
    alignItems: 'center',
    paddingTop: Platform.OS === 'ios' ? 80 : 40,
  },
  modalContent: {
    borderRadius: 12,
    width: '90%',
    maxHeight: '85%',
    ...Platform.select({
      ios: {
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.25,
        shadowRadius: 4,
      },
      android: {
        elevation: 5,
      },
    }),
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
  },
  modalTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  closeButton: {
    padding: 4,
  },
  closeButtonText: {
    fontSize: 24,
  },
  option: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
  },
  optionText: {
    fontSize: 16,
    flex: 1,
  },
  checkmark: {
    fontSize: 20,
    marginLeft: 8,
  },
  searchContainer: {
    borderBottomWidth: 1,
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  searchInput: {
    height: 40,
    fontSize: 16,
  },
  emptyState: {
    padding: 24,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 14,
  },
});
